/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import { useEffect, useState } from "react";
import type { PopperPlacementType, PopperProps } from "@mui/material/Popper";

import { ARROW_SIZE, VIEWPORT_MARGIN } from "./popperConstants";

export interface AvailableSpace {
  width: number | null;
  height: number | null;
}

function getAnchorRect(anchorEl: PopperProps["anchorEl"]): DOMRect | null {
  const anchor = typeof anchorEl === "function" ? anchorEl() : anchorEl;
  if (
    !anchor ||
    typeof (anchor as { getBoundingClientRect?: unknown })
      .getBoundingClientRect !== "function"
  ) {
    return null;
  }
  return (
    anchor as { getBoundingClientRect: () => DOMRect }
  ).getBoundingClientRect();
}

function computeAvailableWidth(
  rect: DOMRect,
  placement: PopperPlacementType
): number {
  const gap = ARROW_SIZE + 4;
  if (placement.startsWith("right")) {
    return window.innerWidth - rect.right - gap - VIEWPORT_MARGIN;
  }
  if (placement.startsWith("left")) {
    return rect.left - gap - VIEWPORT_MARGIN;
  }
  if (placement.endsWith("-start")) {
    return window.innerWidth - rect.left - VIEWPORT_MARGIN;
  }
  if (placement.endsWith("-end")) {
    return rect.right - VIEWPORT_MARGIN;
  }
  return window.innerWidth - VIEWPORT_MARGIN * 2;
}

function computeAvailableHeight(
  rect: DOMRect,
  placement: PopperPlacementType
): number {
  const gap = ARROW_SIZE + 4;
  if (placement.startsWith("top")) {
    return rect.top - gap - VIEWPORT_MARGIN;
  }
  if (placement.startsWith("bottom")) {
    return window.innerHeight - rect.bottom - gap - VIEWPORT_MARGIN;
  }
  if (placement.startsWith("right") || placement.startsWith("left")) {
    const spaceBelow = window.innerHeight - rect.top - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    return Math.max(spaceBelow, spaceAbove);
  }
  return window.innerHeight - VIEWPORT_MARGIN * 2;
}

/**
 * Tracks the available width/height for a Popper anchored to `anchorEl`,
 * given its placement. Recomputes on window resize.
 *
 * Intentionally does NOT reset to null when `enabled` flips false — the
 * Popper stays mounted during fade-out, and null values would briefly
 * stretch the Paper to nearly full viewport height and flash a scrollbar.
 * Cached values get overwritten on the next open.
 */
export function useAvailableSpace(
  anchorEl: PopperProps["anchorEl"],
  placement: PopperPlacementType,
  enabled: boolean
): AvailableSpace {
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const compute = () => {
      const rect = getAnchorRect(anchorEl);
      if (!rect) {
        setWidth(null);
        setHeight(null);
        return;
      }
      setWidth(
        Math.max(200, Math.floor(computeAvailableWidth(rect, placement)))
      );
      setHeight(
        Math.max(150, Math.floor(computeAvailableHeight(rect, placement)))
      );
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [enabled, anchorEl, placement]);

  return { width, height };
}
